import axios from "axios";

import { hasAnalyticsConsent } from "@/lib/privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "minga.admin.token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      setToken(null);
    }
    return Promise.reject(err);
  },
);

// ---------- Public helpers ----------
export const publicApi = {
  getSettings: () => api.get("/public/settings").then((r) => r.data),
  getPage: (slug) => api.get(`/public/pages/${slug}`).then((r) => r.data),
  getProjects: (featured) =>
    api
      .get("/public/projects", { params: featured !== undefined ? { featured } : {} })
      .then((r) => r.data),
  getProject: (slug) => api.get(`/public/projects/${slug}`).then((r) => r.data),
  getTeam: () => api.get("/public/team").then((r) => r.data),
  getTestimonials: () => api.get("/public/testimonials").then((r) => r.data),
  getServices: (group) =>
    api.get("/public/services", { params: group ? { group } : {} }).then((r) => r.data),
  submitLead: (payload) => api.post("/public/leads", payload).then((r) => r.data),
  track: (payload) => {
    if (!hasAnalyticsConsent()) {
      return Promise.resolve({ ok: false, skipped: true });
    }
    return api.post("/public/analytics/track", payload).then((r) => r.data);
  },
};

// ---------- Admin helpers ----------
export const authApi = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};

export const adminApi = {
  metrics: () => api.get("/admin/dashboard/metrics").then((r) => r.data),
  // Leads
  listLeads: (params) => api.get("/admin/leads", { params }).then((r) => r.data),
  updateLead: (id, patch) => api.patch(`/admin/leads/${id}`, patch).then((r) => r.data),
  deleteLead: (id) => api.delete(`/admin/leads/${id}`).then((r) => r.data),
  // Pages
  listPages: () => api.get("/admin/pages").then((r) => r.data),
  getPage: (slug) => api.get(`/admin/pages/${slug}`).then((r) => r.data),
  updatePage: (slug, patch) => api.patch(`/admin/pages/${slug}`, patch).then((r) => r.data),
  // Projects
  listProjects: () => api.get("/admin/projects").then((r) => r.data),
  getProject: (slug) => api.get(`/admin/projects/${slug}`).then((r) => r.data),
  createProject: (data) => api.post("/admin/projects", data).then((r) => r.data),
  updateProject: (slug, patch) =>
    api.patch(`/admin/projects/${slug}`, patch).then((r) => r.data),
  deleteProject: (slug) => api.delete(`/admin/projects/${slug}`).then((r) => r.data),
  // Testimonials
  listTestimonials: () => api.get("/admin/testimonials").then((r) => r.data),
  createTestimonial: (data) => api.post("/admin/testimonials", data).then((r) => r.data),
  updateTestimonial: (id, data) =>
    api.patch(`/admin/testimonials/${id}`, data).then((r) => r.data),
  deleteTestimonial: (id) => api.delete(`/admin/testimonials/${id}`).then((r) => r.data),
  // Team
  listTeam: () => api.get("/admin/team").then((r) => r.data),
  createTeam: (data) => api.post("/admin/team", data).then((r) => r.data),
  updateTeam: (id, data) => api.patch(`/admin/team/${id}`, data).then((r) => r.data),
  deleteTeam: (id) => api.delete(`/admin/team/${id}`).then((r) => r.data),
  // Services
  listServices: () => api.get("/admin/services").then((r) => r.data),
  createService: (data) => api.post("/admin/services", data).then((r) => r.data),
  updateService: (id, data) => api.patch(`/admin/services/${id}`, data).then((r) => r.data),
  deleteService: (id) => api.delete(`/admin/services/${id}`).then((r) => r.data),
  // Media
  listMedia: (params) => api.get("/admin/media", { params }).then((r) => r.data),
  createMedia: (data) => api.post("/admin/media", data).then((r) => r.data),
  updateMedia: (id, data) => api.patch(`/admin/media/${id}`, data).then((r) => r.data),
  deleteMedia: (id) => api.delete(`/admin/media/${id}`).then((r) => r.data),
  // Settings
  getSettings: () => api.get("/admin/settings").then((r) => r.data),
  updateSettings: (patch) => api.patch("/admin/settings", patch).then((r) => r.data),
  // Activity
  activity: (params) => api.get("/admin/activity", { params }).then((r) => r.data),
};
